/*
 * Language Detection & Preference Utility
 * --------------------------------------
 * Implements automatic language selection using the following precedence:
 * 1. User override stored in localStorage (key: app.langPreference)
 * 2. IP-based country detection via https://learn-quran.co/gotolink/get-ip-api-location/ (timeout ~1800ms)
 * 3. Browser language (navigator.languages / navigator.language)
 * 4. Default: English (en)
 *
 * Supported languages: ar (Arabic), id (Indonesian), en (English)
 * Country → Language mapping:
 *  - Arabic if country_code in ARAB_COUNTRIES (Arab League set)
 *  - Indonesian if country_code === 'ID'
 *  - English otherwise
 *
 * Public API:
 *  - detectInitialLanguage(applyFn): Promise<LangCode>
 *  - setLanguage(lang, applyFn): void (persists user override)
 *  - getLanguage(): LangCode (resolves current effective language, preferring stored preference)
 *  - applyLanguage(lang, applyFn?): applies to i18n + <html lang/dir>
 *
 * Notes:
 *  - We DO NOT persist the automatically detected language on first visit; only explicit user changes are stored.
 *  - Network failures / malformed JSON / timeout gracefully fall back to browser language.
 */

import { getIpLocation } from './ipLocation';

/** @typedef {'ar' | 'id' | 'en'} LangCode */

const LS_KEY = 'app.langPreference';
const ARAB_COUNTRIES = new Set([
  'SA','AE','QA','KW','BH','OM','YE','IQ','JO','PS','LB','SY','EG','SD','DZ','MA','TN','LY','MR','SO','DJ','KM'
]);

function readStoredLang() {
  try {
    const v = window.localStorage.getItem(LS_KEY);
    return v === 'ar' || v === 'id' || v === 'en' ? v : null;
  } catch (_) {
    return null;
  }
}

function browserToLang() {
  try {
    const baseList = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
    const list = baseList.filter(Boolean);
    for (const tag of list) {
      const code = (tag || '').toLowerCase();
      if (code.startsWith('ar')) return 'ar';
      if (code.startsWith('id')) return 'id';
      if (code.startsWith('en')) return 'en';
    }
  } catch (_) {
    /* ignore */
  }
  return 'en';
}

async function ipToLang(timeoutMs = 1800) {
  try {
    const data = await getIpLocation(timeoutMs);
    if (!data) return null;
    const cc = (data.countryCode || '').toUpperCase();
    if (!cc) return null;
    if (cc === 'ID') return 'id';
    if (ARAB_COUNTRIES.has(cc)) return 'ar';
    return 'en';
  } catch (_) {
    return null;
  }
}

export async function detectInitialLanguage(applyFn) {
  // 1) Stored override
  const stored = readStoredLang();
  if (stored) {
    applyLanguage(stored, applyFn);
    return stored;
  }
  // 2) IP based
  const fromIp = await ipToLang();
  if (fromIp) {
    applyLanguage(fromIp, applyFn);
    return fromIp;
  }
  // 3) Browser fallback
  const fromBrowser = browserToLang();
  applyLanguage(fromBrowser, applyFn);
  return fromBrowser;
}

export function setLanguage(lang, applyFn) {
  if (lang !== 'ar' && lang !== 'id' && lang !== 'en') return;
  try { window.localStorage.setItem(LS_KEY, lang); } catch (_) { /* ignore */ }
  applyLanguage(lang, applyFn);
}

export function getLanguage() {
  return readStoredLang() || 'en';
}

export function applyLanguage(lang, applyFn) {
  // Normalize to supported
  if (lang !== 'ar' && lang !== 'id' && lang !== 'en') lang = 'en';
  // If an external apply function is provided (e.g., i18n locale setter), call it
  if (typeof applyFn === 'function') {
    applyFn(lang);
  } else if (window.__appI18nInstance) { // optional global reference
    try { window.__appI18nInstance.locale = lang.toUpperCase(); } catch (_) {}
  }
  // Update <html lang> and dir
  try {
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    const isRtl = lang === 'ar';
    html.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  } catch (_) { /* ignore */ }
}

export const __testOnly = { browserToLang, ipToLang, readStoredLang, ARAB_COUNTRIES };
